import { type ReactNode, useEffect } from 'react';
import { animate, motion, useMotionValue, useTransform, type Variants } from 'framer-motion';

// A soft, premium ease (expo-out). Shared so every motion feels of a piece.
const EASE = [0.16, 1, 0.3, 1] as const;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

export const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.055, delayChildren: 0.04 } },
};

/** Stagger its children in. Children should be <Item> (or any motion element
 *  using the `fadeUp` variant). */
export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} initial="hidden" animate="show" variants={staggerContainer}>
      {children}
    </motion.div>
  );
}

/** One staggered item. */
export function Item({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={fadeUp}>
      {children}
    </motion.div>
  );
}

/** Reveal a single block on mount (fade + rise). */
export function Reveal({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

/** Count a number up from 0 → value on mount. */
export function CountUp({ value, suffix = '', decimals = 0 }: { value: number; suffix?: string; decimals?: number }) {
  const mv = useMotionValue(0);
  const text = useTransform(mv, (v) =>
    (decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString()) + suffix,
  );
  useEffect(() => {
    const controls = animate(mv, value, { duration: 1.0, ease: EASE });
    return () => controls.stop();
  }, [value, mv]);
  return <motion.span>{text}</motion.span>;
}

/** Render a string|number stat, counting up the numeric part when present. */
export function AnimatedValue({ value }: { value: string | number }) {
  if (typeof value === 'number') return <CountUp value={value} />;
  const m = /^([\d,]+(?:\.\d+)?)\s*(%|s)?$/.exec(value.trim());
  if (m) {
    const n = parseFloat(m[1].replace(/,/g, ''));
    const decimals = m[1].includes('.') ? 1 : 0;
    return <CountUp value={n} suffix={m[2] ?? ''} decimals={decimals} />;
  }
  return <>{value}</>;
}

/** A track + fill bar whose width animates 0 → pct%. */
export function AnimatedBar({
  pct,
  className = 'bg-primary',
  height = 'h-1.5',
  delay = 0,
}: {
  pct: number;
  className?: string;
  height?: string;
  delay?: number;
}) {
  return (
    <div className={`${height} w-full overflow-hidden rounded-full bg-muted`}>
      <motion.div
        className={`h-full rounded-full ${className}`}
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      />
    </div>
  );
}
